import { FunctionComponent, useReducer, useState } from 'react';
import { useNotification } from '@starjack/features/notifications';
import {
    Autocomplete,
    Card,
    CardContent,
    Divider,
    ListItemButton,
    Stack,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import { CardListRow } from '@/components/Card/CardListRow';
import { LoadingButton } from '@mui/lab';
import { Customer } from '@/models/customer';
import { useGetCreditQuery, usePostCreditMutation } from '@/api/v2/credit';
import { getMoneyAsString } from '@/utils';
import { Select } from '@/components/Select/Select';
import { MoneyInput } from '@/components/MoneyInput/MoneyInput';
import { Money } from '@/models/money';
import { PayloadAction, Action } from '@reduxjs/toolkit';
import { CreditType } from '@/models/credit';
import { useGetAllLiftOperatorsQuery } from '@/api/v2/liftOperator';
import { useIsAuthorized } from '@/hooks/useIsAuthorized';
import { Permission } from '@/models/user';

const selectableCreditTypes: { type: CreditType; label: string }[] = [
    {
        type: 'GOODWILL',
        label: 'Goodwill',
    },
    {
        type: 'SUBTRACT',
        label: 'Abzug',
    },
];

type InputError = Record<string, string>;

type CreditState = {
    balance: Money;
    creditType: CreditType;
    comment: string;
    liftOperator: string;
};

export const defaultState: CreditState = {
    balance: {
        amount: 0,
        currency: 'EUR',
    },
    creditType: 'GOODWILL',
    comment: '',
    liftOperator: '',
};

type CreditAction =
    | PayloadAction<Money, 'SET_BALANCE'>
    | PayloadAction<CreditType, 'SET_CREDIT_TYPE'>
    | PayloadAction<string, 'SET_COMMENT'>
    | Action<'RESET'>
    | PayloadAction<string, 'SET_LIFTOPERATOR'>; // liftoperator id

const reducer = (state: CreditState, action: CreditAction): CreditState => {
    switch (action.type) {
        case 'SET_BALANCE':
            return {
                ...state,
                balance: action.payload,
            };
        case 'SET_CREDIT_TYPE':
            return {
                ...state,
                creditType: action.payload,
            };
        case 'SET_COMMENT':
            return {
                ...state,
                comment: action.payload,
            };
        case 'RESET':
            return defaultState;
        case 'SET_LIFTOPERATOR':
            return {
                ...state,
                liftOperator: action.payload === '-' ? '' : action.payload,
            };
        default:
            return state;
    }
};

type CustomerCreditCardProps = {
    customer: Customer;
};

export const CustomerCreditCard: FunctionComponent<CustomerCreditCardProps> = ({
    customer,
}) => {
    const { data: liftOperators = [], isLoading: liftOperatorsLoading } =
        useGetAllLiftOperatorsQuery();
    const { data: credit } = useGetCreditQuery({ customerId: customer.id });
    const { addNotification } = useNotification();
    const [state, dispatch] = useReducer(reducer, defaultState);
    const [postCredit, { isLoading: creditLoading }] = usePostCreditMutation();
    const [errors, setErrors] = useState<InputError>({});
    const [isAuthorized] = useIsAuthorized([Permission.ADMIN]);

    const liftOperatorsAutoCompletOptions = liftOperators.map(
        (liftOperator) => ({ id: liftOperator.id, label: liftOperator.name.de })
    );

    const onAddCredit = () => {
        const { creditType, balance, comment, liftOperator } = state;

        if (balance.amount < 1) {
            setErrors((prev) => ({
                ...prev,
                balance: 'Wert muss positiv sein',
            }));

            return;
        }

        if (comment.length < 10) {
            setErrors((prev) => ({
                ...prev,
                comment: 'Kommentar muss mind. 10 Zeichen lang sein',
            }));

            return;
        }

        setErrors({});

        postCredit({
            customerId: customer.id,
            type: creditType,
            amount: {
                ...balance,
                amount:
                    creditType === 'SUBTRACT'
                        ? -balance.amount
                        : balance.amount,
            },
            comment: comment,
            liftOperatorId: liftOperator,
        })
            .unwrap()
            .then(() => {
                addNotification({
                    type: 'success',
                    message: 'Kredit erfolgreich hinzugefügt',
                });
                dispatch({ type: 'RESET' });
            })
            .catch(() => {
                addNotification({
                    type: 'error',
                    message:
                        'Fehler beim Speichern. Bitte versuchen Sie es später noch einmal',
                });
            });
    };

    const collectedBalance =
        credit?.balances.reduce<
            Record<string, { liftOperatorId: string; amount: Money }>
        >((balance, currentCredit) => {
            const liftOperatorId = currentCredit.liftOperatorId || 'Portal';

            if (!balance[liftOperatorId]) {
                balance[liftOperatorId] = {
                    liftOperatorId,
                    amount: currentCredit.balance,
                };
            } else {
                balance[liftOperatorId] = {
                    ...balance[liftOperatorId],
                    amount: {
                        amount:
                            balance[liftOperatorId].amount.amount +
                            currentCredit.balance.amount,
                        currency: currentCredit.balance.currency,
                    },
                };
            }

            return balance;
        }, {}) || {};

    return (
        <Card
            sx={{
                maxWidth: '640px',
            }}
        >
            <CardContent
                sx={{
                    bgcolor: 'divider',
                }}
            >
                <Typography variant='h6'>Guthaben</Typography>
            </CardContent>
            <Divider
                sx={{
                    width: '100%',
                }}
            />

            <CardListRow
                sx={{
                    position: 'relative',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Typography variant='subtitle2'>Gesamt:</Typography>
                {!!credit && (
                    <Typography
                        sx={{
                            mt: 2,
                            ml: 2,
                        }}
                    >
                        {getMoneyAsString(credit.balance)}
                    </Typography>
                )}
            </CardListRow>

            {Object.values(collectedBalance).map((balanceEntry, index) => (
                <CardListRow
                    key={index}
                    sx={{
                        position: 'relative',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Typography variant='subtitle2'>
                        {balanceEntry.liftOperatorId !== 'Portal'
                            ? liftOperators?.find(
                                  (l) => l.id === balanceEntry.liftOperatorId
                              )?.name.de || 'Bergbahn'
                            : 'Portal'}
                    </Typography>
                    <Typography
                        sx={{
                            mt: 2,
                            ml: 2,
                        }}
                    >
                        {getMoneyAsString(balanceEntry.amount)}
                    </Typography>
                </CardListRow>
            ))}

            {isAuthorized && (
                <CardContent>
                    <Autocomplete<{ label: string; id: string }>
                        sx={{
                            mb: 2,
                            width: 348,
                        }}
                        options={liftOperatorsAutoCompletOptions}
                        getOptionKey={(option) => option.id}
                        onChange={(_, value) => {
                            dispatch({
                                type: 'SET_LIFTOPERATOR',
                                payload: value?.id || null,
                            });
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label='Bergbahnen' />
                        )}
                        isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                        }
                        loading={liftOperatorsLoading}
                        loadingText={
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                }}
                            >
                                Bergbahnen werden geladen
                            </Typography>
                        }
                        renderOption={(props, option) => (
                            <ListItemButton component='li' {...props}>
                                {option.label}
                            </ListItemButton>
                        )}
                        ListboxComponent={(props) => (
                            <Paper
                                {...props}
                                sx={{
                                    background: '#f5f5f5',
                                }}
                                elevation={4}
                            />
                        )}
                    />

                    <Stack spacing={2} direction='row' alignItems='flex-start'>
                        <Select
                            sx={{
                                width: 180,
                            }}
                            label='Guthabenart'
                            value={state.creditType}
                            onChange={(e) => {
                                const { value } = e.target;

                                dispatch({
                                    type: 'SET_CREDIT_TYPE',
                                    payload: value as CreditType,
                                });
                            }}
                        >
                            {selectableCreditTypes.map(({ type, label }) => (
                                <option key={type} value={type}>
                                    {label}
                                </option>
                            ))}
                        </Select>

                        <MoneyInput
                            sx={{
                                width: 160,
                            }}
                            label='Wert'
                            value={state.balance}
                            error={!!errors.balance}
                            helperText={'Wert muss positiv sein'}
                            onChange={(nextVal) => {
                                dispatch({
                                    type: 'SET_BALANCE',
                                    payload: {
                                        ...nextVal,
                                        amount: Math.abs(nextVal.amount),
                                    },
                                });
                            }}
                        />

                        <TextField
                            sx={{
                                flexGrow: 1,
                            }}
                            value={state.comment}
                            error={!!errors.comment}
                            label='Kommentar'
                            helperText={'Mind. 10 Zeichen'}
                            onChange={(e) => {
                                const { value } = e.target;

                                dispatch({
                                    type: 'SET_COMMENT',
                                    payload: value,
                                });
                            }}
                        />
                    </Stack>

                    <LoadingButton
                        sx={{
                            mt: 2,
                        }}
                        variant='contained'
                        disabled={
                            state.comment.length < 10 ||
                            state.balance.amount < 1
                        }
                        loading={creditLoading}
                        onClick={onAddCredit}
                    >
                        Guthaben buchen
                    </LoadingButton>
                </CardContent>
            )}
        </Card>
    );
};
